/**
 * Combat UI Component
 * 
 * Handles the full combat flow with bestiary-based monster stats.
 * Displays dice rolls, damage, and fight/flee decisions.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Player, Monster, CombatState } from '../types/player';
import { useCombat } from '../hooks/useCombat';
import { PartySize } from '../utils/combat';
import { usePlayer } from '../contexts/PlayerContext';
import { FolkloreContainer } from './FolkloreContainer';
import './CombatUI.css';

export interface CombatUIProps {
  monsterName: string;
  party: Player[];
  partySize: PartySize;
  onCombatEnd: (result: 'victory' | 'defeat' | 'fled') => void;
  onFlee?: () => void;
  onCombatStateUpdate?: (state: CombatState | undefined) => void;
}

export function CombatUI({
  monsterName,
  party,
  partySize,
  onCombatEnd,
  onFlee,
  onCombatStateUpdate,
}: CombatUIProps): React.ReactElement {
  const { combatState, startCombat, executeRound, flee, reset } = useCombat();
  const { player, takeDamage, heal } = usePlayer();
  const [isRolling, setIsRolling] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [roundResult, setRoundResult] = useState<any>(null);
  const logRef = useRef<HTMLDivElement>(null);

  // Initialize combat on mount
  useEffect(() => {
    // DEBUG: Log combat initialization
    console.log('⚔️ CombatUI MOUNT - Checking combat state:', {
      hasCombatState: !!combatState,
      isActive: combatState?.isActive,
      round: combatState?.round,
      currentMonster: combatState?.monster?.name,
      expectedMonster: monsterName,
      matches: combatState?.monster?.name === monsterName,
    });
    
    // Guard: Don't initialize combat if monsterName is empty
    if (!monsterName || monsterName.trim() === '') {
      console.error('⚔️ CombatUI - ERROR: Cannot initialize combat with empty monster name');
      return;
    }
    
    // Only start combat if:
    // 1. No combat state exists, OR
    // 2. Combat state exists but is for a different monster (shouldn't happen, but safety check)
    // This preserves existing combat state (including round number) when loaded from Firestore
    if (!combatState || !combatState.isActive || combatState.monster?.name !== monsterName) {
      console.log('⚔️ CombatUI - Starting NEW combat (no existing state or mismatch)');
      startCombat(monsterName, party, partySize);
    } else {
      console.log('⚔️ CombatUI - PRESERVING existing combat state (round', combatState.round, ')');
    }
    // Don't reset combat state on unmount - combat state is managed by PlayerContext
    // Only reset if we're intentionally ending combat (via onCombatEnd or onFlee)
    return () => {
      console.log('⚔️ CombatUI UNMOUNT - NOT resetting combat state (preserving for reloads)');
    };
  }, [monsterName, partySize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pass combat state updates to parent
  useEffect(() => {
    if (onCombatStateUpdate) {
      onCombatStateUpdate(combatState || undefined);
    }
  }, [combatState, onCombatStateUpdate]);

  // Auto-scroll combat log to bottom when storyLines update
  useEffect(() => {
    if (logRef.current && combatState?.storyLines) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [combatState?.storyLines]);

  // Format dice display - show dice faces for 1-6, numbers for higher values
  const formatDice = (roll: number): string => {
    // Standard dice faces for 1-6
    const diceFaces = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];
    // For values 7+, show the number instead
    if (roll >= 1 && roll <= 6) {
      return diceFaces[roll - 1];
    } else if (roll >= 7 && roll <= 10) {
      // For values 7-10, display as a number in a box or use a different representation
      return `[${roll}]`;
    }
    return '⚀'; // Fallback
  };

  const handleFight = async () => {
    if (!combatState || !combatState.isActive) return;

    setIsRolling(true);
    setShowResult(false);

    // Small delay for animation
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      const result = await executeRound(); // Await the Firestore save
      setRoundResult(result);

      // Sync party HP from combat result to PlayerContext
      // The useCombat hook updates party HP in combatState, we sync it to PlayerContext
      // Use the result data which has the updated HP values
      if (result.partyHpAfter && result.partyHpAfter.length > 0) {
        const newPlayerHp = result.partyHpAfter[0];
        const currentPlayerHp = player?.hp || 0;
        
        // Only update if different (avoid unnecessary updates)
        if (currentPlayerHp !== newPlayerHp) {
          const hpDiff = newPlayerHp - currentPlayerHp;
          if (hpDiff < 0) {
            takeDamage(Math.abs(hpDiff));
          } else if (hpDiff > 0) {
            heal(hpDiff);
          }
        }
      }

      setIsRolling(false);
      setShowResult(true);

      // Allow user to continue fighting immediately after result is shown
      // (unless combat ended)
      if (!result.isMonsterDefeated && !result.areAllPlayersDefeated) {
        // Reset showResult immediately so user can click "Fight Again"
        // Use a tiny delay to ensure state updates properly
        setTimeout(() => {
          setShowResult(false);
        }, 100);
      }

      // Check win/loss conditions
      if (result.isMonsterDefeated) {
        setTimeout(() => {
          onCombatEnd('victory');
          reset();
        }, 2000);
      } else if (result.areAllPlayersDefeated) {
        setTimeout(() => {
          onCombatEnd('defeat');
          reset();
        }, 2000);
      }
    } catch (error) {
      console.error('Combat error:', error);
      setIsRolling(false);
    }
  };

  const handleFlee = () => {
    flee();
    if (onFlee) {
      onFlee();
    }
    setTimeout(() => {
      onCombatEnd('fled');
      reset();
    }, 1000);
  };

  if (!combatState || !combatState.monster) {
    return (
      <div className="combat-ui">
        <FolkloreContainer variant="story">
          <p>Initializing combat...</p>
        </FolkloreContainer>
      </div>
    );
  }

  const monster = combatState.monster;
  const currentParty = combatState.party || party;
  const playerRolls = combatState.playerRolls || [];
  const partyTotal = combatState.partyTotal || 0;
  const monsterAttack = combatState.monsterAttack || monster.attack;
  const damage = combatState.damage || 0;
  const winner = combatState.winner;

  const isSoloPlayer = party.length === 1;
  const useNewCombatSystem = isSoloPlayer && monster.attackDie && monster.damageDie;

  return (
    <div className="combat-ui">
      {/* Combat Round Display */}
      <div className="combat-round">
        <FolkloreContainer variant="story" title={`Round ${combatState.round}`}>
          {combatState.round === 0 ? (
            <>
              <p className="combat-intro">
                You encounter a <strong>{monster.name}</strong>! Prepare for combat.
                {useNewCombatSystem && (
                  <>
                    {' '}Combat uses a two-dice system: <strong>Attack Die (<img src="/assets/ui/attack-score.svg" alt="Attack" style={{ width: '20px', height: '20px', verticalAlign: 'middle', display: 'inline-block' }} />)</strong> determines who hits (higher roll wins), <strong>Damage Die (<img src="/assets/ui/damage-score.svg" alt="Damage" style={{ width: '20px', height: '20px', verticalAlign: 'middle', display: 'inline-block' }} />)</strong> determines damage dealt by the winner.
                  </>
                )}
              </p>
              {/* Action Buttons - Integrated into combat round */}
              <div className="combat-actions">
                {combatState.isActive && (
                  <>
                    <button
                      className="combat-button fight-button"
                      onClick={handleFight}
                      disabled={isRolling}
                    >
                      Start Combat
                    </button>
                    {combatState.canFlee && onFlee && (
                      <button
                        className="combat-button flee-button"
                        onClick={handleFlee}
                        disabled={isRolling}
                      >
                        Flee
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Combat Log - Single ongoing battle log with auto-scroll */}
              <div className="combat-log" ref={logRef}>
                {isRolling && (
                  <p className="log-line" style={{ opacity: 0.5 }}>
                    Rolling dice...
                  </p>
                )}
                {combatState.storyLines.map((line, index) => (
                  <p key={index} className="log-line">{line}</p>
                ))}
              </div>

              {/* Action Buttons - Integrated into combat round */}
              <div className="combat-actions">
                {combatState.isActive && (
                  <>
                    <button
                      className="combat-button fight-button"
                      onClick={handleFight}
                      disabled={isRolling}
                    >
                      Fight Again
                    </button>
                    {combatState.canFlee && onFlee && (
                      <button
                        className="combat-button flee-button"
                        onClick={handleFlee}
                        disabled={isRolling}
                      >
                        Flee
                      </button>
                    )}
                  </>
                )}
                {!combatState.isActive && (
                  <div className="combat-ended">
                    {combatState.monster.currentHp <= 0 ? (
                      <p className="victory-message">Victory! The {monster.name} is defeated!</p>
                    ) : currentParty[0]?.hp <= 0 ? (
                      <p className="defeat-message">💀 You have been defeated...</p>
                    ) : (
                      <p className="flee-message">🏃 You fled from combat.</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </FolkloreContainer>
      </div>
    </div>
  );
}

